// Bankafschriften upload + lijst. PRD-09 §6.

import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";

interface BankDoc {
  id: string;
  format: string;
  status: "UPLOADED" | "PROCESSING" | "COMPLETED" | "PARTIAL" | "FAILED";
  detectedBank: string | null;
  periodStart: string | null;
  periodEnd: string | null;
  transactionCount: number;
  errorMessage: string | null;
  createdAt: string;
}

interface Transaction {
  id: string;
  bookingDate: string;
  amount: string;
  currency: string;
  description: string;
  counterpartyName: string | null;
  transactionType: string;
  category: string;
  categoryConfidence: number | null;
  isOwnTransfer: boolean;
  matchedInvoiceId: string | null;
}

const CATEGORY_COLOR: Record<string, string> = {
  INCOME: "bg-green-50 text-green-800",
  BENEFIT: "bg-green-50 text-green-800",
  FACTUUR: "bg-orange-50 text-orange-800",
  SUBSCRIPTION: "bg-blue-50 text-blue-800",
  PURCHASE: "bg-neutral-100 text-neutral-700",
  TRANSFER_OWN: "bg-neutral-50 text-neutral-500",
  TAX: "bg-red-50 text-red-800",
  CASH: "bg-amber-50 text-amber-800",
  FEE: "bg-amber-50 text-amber-800",
  OTHER: "bg-neutral-100 text-neutral-600",
};

export function BankStatementsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [hideOwnTransfers, setHideOwnTransfers] = useState(true);

  const docsQuery = useQuery({
    queryKey: ["bank-statements"],
    queryFn: () => api.get<{ documents: BankDoc[] }>("/v1/bank-statements"),
    refetchInterval: 5_000,
  });

  const txQuery = useQuery({
    queryKey: ["bank-transactions"],
    queryFn: () => api.get<{ transactions: Transaction[] }>("/v1/bank-statements/transactions"),
    refetchInterval: 10_000,
  });

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const buffer = await file.arrayBuffer();
      const res = await fetch("/api/v1/bank-statements/upload", {
        method: "POST",
        headers: {
          "Content-Type": "application/octet-stream",
          "X-Filename": file.name,
          "X-Content-Type": file.type || "application/octet-stream",
          Authorization: `Bearer ${sessionStorage.getItem("bb:access") ?? ""}`,
        },
        body: buffer,
      });
      const json = await res.json();
      if (!json.success) {
        throw new ApiError(json.error.code, json.error.i18nKey, res.status, json.error.details);
      }
      return json.data;
    },
    onSuccess: () => {
      setErrorKey(null);
      qc.invalidateQueries({ queryKey: ["bank-statements"] });
      qc.invalidateQueries({ queryKey: ["bank-transactions"] });
    },
    onError: (e) => setErrorKey(e instanceof ApiError ? e.i18nKey : "errors.internal"),
  });

  const transactions = (txQuery.data?.transactions ?? []).filter(
    (t) => !hideOwnTransfers || !t.isOwnTransfer
  );

  return (
    <main className="min-h-screen bg-neutral-50">
      <header className="bg-white border-b border-neutral-200 px-4 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <h1 className="text-xl font-semibold">{t("bank.title", "Bankafschriften")}</h1>
          <button
            onClick={() => navigate("/dashboard")}
            className="text-sm text-neutral-600 hover:text-neutral-900"
          >
            ← {t("back", "Terug")}
          </button>
        </div>
      </header>

      <section className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        <div className="bg-white border border-neutral-200 rounded-xl p-5">
          <h2 className="font-semibold mb-2">
            {t("bank.upload.title", "Bankafschrift uploaden")}
          </h2>
          <p className="text-sm text-neutral-600 mb-4">
            {t(
              "bank.upload.subtitle",
              "Upload een CSV-, MT940-, CAMT.053- of PDF-afschrift van ING, ABN AMRO, Rabobank of een andere Nederlandse bank. Een ingescande PDF werkt nog niet — daar is tekstherkenning voor nodig."
            )}
          </p>
          <input
            ref={fileInput}
            type="file"
            /**
             * Wat de verwerking werkelijk aankan. Hier stond `.csv,.txt`,
             * terwijl de backend MT940, CAMT.053 en PDF net zo goed leest. Een
             * cliënt die een `.940` van zijn bank downloadt zag dat bestand
             * grijs in de bestandskiezer en kon niet verder — zonder foutmelding,
             * want er gebeurde niets.
             */
            accept=".csv,.txt,.940,.sta,.swi,.mt940,.xml,.pdf"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) upload.mutate(f);
            }}
          />
          <button
            onClick={() => fileInput.current?.click()}
            disabled={upload.isPending}
            className="px-4 py-2 bg-brand-500 hover:bg-brand-600 disabled:opacity-60 text-white rounded-lg font-medium"
          >
            {upload.isPending ? t("bank.upload.uploading", "Uploaden…") : t("bank.upload.select", "Selecteer bestand")}
          </button>
          {errorKey && (
            <div className="mt-3 px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800">
              {t(errorKey)}
            </div>
          )}
        </div>

        <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
          <header className="px-5 py-3 border-b border-neutral-200">
            <h3 className="font-semibold">{t("bank.documents.title", "Geüploade afschriften")}</h3>
          </header>
          {docsQuery.isLoading ? (
            <p className="p-5 text-sm text-neutral-500">{t("loading", "Laden…")}</p>
          ) : docsQuery.data?.documents.length === 0 ? (
            <p className="p-5 text-sm text-neutral-500">
              {t("bank.documents.empty", "Nog geen afschriften geüpload.")}
            </p>
          ) : (
            <ul className="divide-y divide-neutral-100">
              {docsQuery.data?.documents.map((d) => (
                <li key={d.id} className="px-5 py-3 text-sm flex items-center justify-between">
                  <div>
                    <p className="font-medium">{d.detectedBank ?? d.format}</p>
                    <p className="text-xs text-neutral-500">
                      {d.periodStart && d.periodEnd
                        ? `${new Date(d.periodStart).toLocaleDateString("nl-NL")} → ${new Date(d.periodEnd).toLocaleDateString("nl-NL")}`
                        : t("bank.documents.processing", "Verwerken…")}
                      {d.transactionCount > 0 && ` · ${d.transactionCount} transacties`}
                    </p>
                    {d.errorMessage && (
                      <p className="text-xs text-red-700 mt-1">{d.errorMessage}</p>
                    )}
                  </div>
                  <span
                    className={`text-xs px-2 py-0.5 rounded ${
                      d.status === "COMPLETED"
                        ? "bg-green-50 text-green-800"
                        : d.status === "FAILED"
                          ? "bg-red-50 text-red-800"
                          : "bg-amber-50 text-amber-800"
                    }`}
                  >
                    {d.status}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
          <header className="px-5 py-3 border-b border-neutral-200 flex items-center justify-between">
            <h3 className="font-semibold">
              {t("bank.transactions.title", "Transacties")}{" "}
              <span className="text-xs text-neutral-500 font-normal">
                {transactions.length}
              </span>
            </h3>
            <label className="text-xs flex items-center gap-1">
              <input
                type="checkbox"
                checked={hideOwnTransfers}
                onChange={(e) => setHideOwnTransfers(e.target.checked)}
              />
              {t("bank.transactions.hideOwn", "Verberg eigen overboekingen")}
            </label>
          </header>
          {transactions.length === 0 ? (
            <p className="p-5 text-sm text-neutral-500">
              {t("bank.transactions.empty", "Geen transacties zichtbaar.")}
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 text-neutral-600 text-xs">
                <tr>
                  <th className="text-left px-5 py-2">{t("bank.transactions.col.date", "Datum")}</th>
                  <th className="text-left px-3 py-2">{t("bank.transactions.col.party", "Tegenpartij")}</th>
                  <th className="text-left px-3 py-2">{t("bank.transactions.col.description", "Omschrijving")}</th>
                  <th className="text-right px-3 py-2">{t("bank.transactions.col.amount", "Bedrag")}</th>
                  <th className="px-3 py-2">{t("bank.transactions.col.category", "Categorie")}</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((tx) => {
                  const amt = Number(tx.amount);
                  return (
                    <tr key={tx.id} className="border-t border-neutral-100 hover:bg-neutral-50">
                      <td className="px-5 py-2 text-xs whitespace-nowrap">
                        {new Date(tx.bookingDate).toLocaleDateString("nl-NL", {
                          day: "2-digit",
                          month: "2-digit",
                        })}
                      </td>
                      <td className="px-3 py-2 text-xs">{tx.counterpartyName ?? "–"}</td>
                      <td className="px-3 py-2 text-xs text-neutral-600 truncate max-w-md">
                        {tx.description}
                      </td>
                      <td
                        className={`px-3 py-2 text-right font-mono text-xs whitespace-nowrap ${amt < 0 ? "text-red-700" : "text-green-700"}`}
                      >
                        {amt < 0 ? "–" : "+"}€{Math.abs(amt).toFixed(2)}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <span
                          className={`text-xs px-1.5 py-0.5 rounded ${CATEGORY_COLOR[tx.category] ?? "bg-neutral-100"}`}
                        >
                          {tx.category}
                        </span>
                        {tx.matchedInvoiceId && (
                          <span className="ml-1 text-xs text-green-600" title="Gekoppeld aan factuur">
                            ✓
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <p className="text-xs text-neutral-500">
          <Link to="/dashboard" className="hover:text-neutral-900">
            ← {t("back", "Terug naar dashboard")}
          </Link>
        </p>
      </section>
    </main>
  );
}
